import React, { useEffect, useRef, useState } from "react";
import { fetchPlaces } from "./Places";
import { postcodeWithSpace } from "./PlaceSearch";
import "./PostcodeInput.css";

// A postcode box that suggests whole postcodes as you type, and nothing else: no streets,
// areas or stations. Used by the landing page's property search, the Property lookup
// page (/property) and the Add a property page's "find the home myself". It asks the same place lookup as the map's search
// (/place2, which holds every postcode) and keeps only the whole postcodes that start
// with what was typed. Picking one calls onPick with it, so the page can go straight on.
//
// Every other prop goes to the input itself.

// How many postcodes the list shows
const MAX_SHOWN = 8;
// Wait this long after a keypress before asking the server
const DEBOUNCE_MS = 150;

const FULL_POSTCODE = /^[A-Z]{1,2}\d[A-Z\d]? \d[A-Z]{2}$/;

// What to ask the server for: the typed text in upper case, with the space put in when it
// was typed without one ("n51g" -> "N5 1G"), or null when it can't be the start of a postcode
export const postcodePrefix = (text) => {
    const raw = (text || '').trim().toUpperCase().replace(/\s+/g, ' ');
    const t = postcodeWithSpace(raw) || raw;
    if (t.length < 2 || !/^[A-Z]{1,2}\d[A-Z\d]?( \d?[A-Z]{0,2})?$/.test(t)) return null;
    return t;
};

// The whole postcodes among the places that start with the prefix, in order
export const postcodeMatches = (places, prefix) => {
    const seen = new Set();
    return places.map((p) => p.name)
        .filter((n) => FULL_POSTCODE.test(n) && n.startsWith(prefix) && !seen.has(n) && seen.add(n))
        .sort((a, b) => a.localeCompare(b, 'en', {numeric:true}))
        .slice(0, MAX_SHOWN);
};

const PostcodeInput = ({id, value, onChange, onPick, className, ...rest}) => {
    const [list, setList] = useState([]);
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(-1);
    const request = useRef(0);
    const typed = useRef(false);
    const listId = id + '-pcs';

    useEffect(() => {
        if (!typed.current) return undefined;
        const prefix = postcodePrefix(value);
        // The server needs three characters; "N5" is asked as "N5 " so it finds N5's postcodes
        const ask = prefix && prefix.length < 3 ? prefix + ' ' : prefix;
        const id = ++request.current;
        // A whole postcode still asks, so it shows as the one suggestion to pick
        if (!prefix) { setList([]); return undefined; }
        const timer = setTimeout(async () => {
            const found = await fetchPlaces(ask, '');
            if (id !== request.current) return;
            setList(postcodeMatches(found, prefix.length < 3 ? prefix + ' ' : prefix));
            setActive(-1);
        }, DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [value]);

    const pick = (pc) => {
        typed.current = false;
        request.current++;
        setList([]);
        setOpen(false);
        onPick(pc);
    };

    const onKeyDown = (e) => {
        const shown = open && list.length > 0;
        if (e.key === 'ArrowDown' && list.length) {
            e.preventDefault();
            setOpen(true);
            setActive((a) => (shown ? (a + 1) % list.length : 0));
        } else if (e.key === 'ArrowUp' && shown) {
            e.preventDefault();
            setActive((a) => (a <= 0 ? list.length - 1 : a - 1));
        } else if (e.key === 'Enter' && shown && active >= 0) {
            e.preventDefault();
            pick(list[active]);
        } else if (e.key === 'Escape' && shown) {
            e.preventDefault();
            setOpen(false);
        }
    };

    const shown = open && list.length > 0;
    return (
        <div className="pcac">
            <input {...rest} id={id} className={className} type="text" value={value} autoComplete="off" spellCheck={false}
                role="combobox" aria-autocomplete="list" aria-expanded={shown} aria-controls={listId}
                aria-activedescendant={shown && active >= 0 ? listId + '-' + active : undefined}
                onChange={(e) => { typed.current = true; setOpen(true); onChange(e); }}
                onKeyDown={onKeyDown} onFocus={() => setOpen(true)} onBlur={() => setOpen(false)}/>
            {shown && <ul className="pcac-list" id={listId} role="listbox" aria-label="Postcodes">
                {list.map((pc, i) => (
                    <li key={pc} id={listId + '-' + i} role="option" aria-selected={i === active}
                        className={i === active ? 'on' : undefined}
                        // mousedown, so the pick lands before the input's blur closes the list
                        onMouseDown={(e) => { e.preventDefault(); pick(pc); }}
                        onMouseEnter={() => setActive(i)}>{pc}</li>
                ))}
            </ul>}
        </div>
    );
};

export default PostcodeInput;
